import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { BookOpen, Search, ArrowRight, FileDown, Plus, Check, ShoppingBasket, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Flipbook } from "@/components/Flipbook";
import { QuoteForm } from "@/components/QuoteForm";
import { RevealLine, FadeUp } from "@/components/Kinetic";
import { API, PANTHER_LINKS } from "@/lib/api";
import catalogData from "@/data/catalogData.json";

const pad = (n) => String(n).padStart(3, "0");
const SEL_KEY = "lsc-selection";

const ProductCard = ({ product, onOpenPage, testid, selected, selectedRefs = [], onToggle, onToggleRef }) => {
  const multi = product.refs.length > 1;
  const selCount = selectedRefs.length;
  const allSelected = multi && selCount === product.refs.length;
  return (
  <article
    data-testid={testid}
    className={`bg-panel border p-6 flex flex-col justify-between gap-5 hover:-translate-y-1 transition-all duration-300 group ${
      selected ? "border-blaze" : "border-white/10 hover:border-blaze/60"
    }`}
  >
    <div>
      <div className="h-40 bg-white flex items-center justify-center overflow-hidden mb-5" data-testid={`product-img-wrap-${product.refs[0]}`}>
        <img
          src={product.img}
          alt={product.name}
          loading="lazy"
          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
          data-testid={`product-img-${product.refs[0]}`}
        />
      </div>
      <h3 className="uppercase text-sm font-semibold tracking-wide text-white leading-snug">
        {product.name}
      </h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {product.refs.map((r) => {
          const refSel = selectedRefs.includes(r);
          return (
            <button
              key={r}
              type="button"
              onClick={() => onToggleRef(product, r)}
              data-testid={`product-ref-${r}`}
              title={refSel ? "Retirer cette référence de ma demande" : "Ajouter cette référence à ma demande"}
              className={`font-mono text-[11px] tracking-widest px-2 py-1 border transition-all duration-200 ${
                refSel
                  ? "bg-blaze border-blaze text-white"
                  : "text-blaze border-blaze/40 hover:border-blaze hover:bg-blaze/10"
              }`}
            >
              {refSel ? <Check className="inline h-3 w-3 mr-1 -mt-0.5" /> : null}
              {r}
            </button>
          );
        })}
      </div>
      {multi && (
        <p className="mt-3 font-accent uppercase tracking-[0.18em] text-[10px] text-white/40">
          Cliquez sur une référence pour l'ajouter à votre demande
        </p>
      )}
      {product.prices.length > 0 && (
        <p className="mt-4 text-xs text-white/45 font-accent uppercase tracking-[0.18em]">
          Tarifs sur devis — <span className="text-white/85 text-sm normal-case tracking-normal font-body">via Ligéria Sport Concept</span>
        </p>
      )}
    </div>
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-white/35">P. {product.page}</span>
        <button
          onClick={() => onOpenPage(product.page)}
          data-testid={`product-open-page-${product.page}`}
          className="flex items-center gap-2 font-accent uppercase tracking-[0.2em] text-xs text-white/70 hover:text-blaze transition-colors"
        >
          <BookOpen className="h-4 w-4" /> Voir la page
        </button>
      </div>
      <button
        onClick={() => onToggle(product)}
        data-testid={`product-select-${product.refs[0]}`}
        className={`w-full flex items-center justify-center gap-2 font-accent uppercase tracking-[0.2em] text-xs px-4 py-3 border transition-all duration-200 ${
          selected
            ? "bg-blaze border-blaze text-white"
            : "border-white/20 text-white/80 hover:border-blaze hover:text-blaze"
        }`}
      >
        {allSelected ? <X className="h-4 w-4" /> : selected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        {allSelected
          ? "Retirer toutes les références"
          : multi
            ? selected
              ? `Compléter la sélection (${selCount}/${product.refs.length})`
              : "Ajouter toutes les références"
            : selected
              ? "Dans ma demande"
              : "Ajouter à ma demande"}
      </button>
    </div>
  </article>
  );
};

const SectionNav = ({ sections, activeIdx, onSelect }) => {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const t = setTimeout(update, 400);
    window.addEventListener("resize", update);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", update);
    };
  }, [update, sections]);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        onScroll={update}
        className="mx-auto max-w-7xl px-5 md:px-10 flex gap-2 overflow-x-auto no-scrollbar py-3"
        data-testid="catalogue-section-scroller"
      >
        {sections.map((s, i) => (
          <button
            key={s.num}
            data-testid={`section-pill-${s.num}`}
            onClick={() => onSelect(i)}
            className={`shrink-0 font-accent uppercase tracking-[0.18em] text-xs px-4 py-2 border transition-colors duration-200 ${
              i === activeIdx
                ? "bg-blaze border-blaze text-white"
                : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
            }`}
          >
            {String(s.num).padStart(2, "0")} · {s.name}
          </button>
        ))}
      </div>

      {canLeft && (
        <>
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-black to-transparent pointer-events-none" aria-hidden="true" />
          <button
            onClick={() => nudge(-1)}
            data-testid="section-nav-left"
            aria-label="Catégories précédentes"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-black/90 border border-white/25 text-white hover:border-blaze hover:text-blaze transition-colors duration-200"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </>
      )}

      {canRight && (
        <>
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-black to-transparent pointer-events-none" aria-hidden="true" />
          <button
            onClick={() => nudge(1)}
            data-testid="section-nav-right"
            aria-label="Catégories suivantes — aller au bout du catalogue"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-black/90 border border-white/25 text-white hover:border-blaze hover:text-blaze transition-colors duration-200"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
};

export default function Catalogue() {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [flip, setFlip] = useState({ open: false, page: 1 });
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(SEL_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const persist = (sel) => {
    setSelection(sel);
    sessionStorage.setItem(SEL_KEY, JSON.stringify(sel));
  };

  const removeRow = (i) => {
    persist(selection.filter((_, idx) => idx !== i));
  };

  const removeRowByRef = (ref) => {
    persist(selection.filter((s) => s.ref !== ref));
  };

  const toggleRef = (product, ref) => {
    const exists = selection.find((s) => s.ref === ref);
    if (exists) persist(selection.filter((s) => s.ref !== ref));
    else persist([...selection, { ref, name: product.name, page: product.page, qty: "" }]);
  };

  const toggleSelection = (product) => {
    const prodRefs = product.refs;
    const allSelected = prodRefs.every((r) => selection.some((s) => s.ref === r));
    if (allSelected) {
      persist(selection.filter((s) => !prodRefs.includes(s.ref)));
    } else {
      const toAdd = prodRefs
        .filter((r) => !selection.some((s) => s.ref === r))
        .map((r) => ({ ref: r, name: product.name, page: product.page, qty: "" }));
      persist([...selection, ...toAdd]);
    }
  };

  const setSelectionQty = (i, qty) => {
    persist(selection.map((s, idx) => (idx === i ? { ...s, qty } : s)));
  };

  const setRowRef = (i, ref) => {
    persist(selection.map((s, idx) => (idx === i ? { ...s, ref } : s)));
  };

  const addRow = () => {
    persist([...selection, { ref: "", name: "", page: null, qty: "" }]);
  };

  const sections = catalogData.sections;

  const openPage = (p) => setFlip({ open: true, page: p });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const out = [];
    for (const [pg, prods] of Object.entries(catalogData.pages)) {
      for (const p of prods) {
        if (p.name.toLowerCase().includes(q) || p.refs.some((r) => r.toLowerCase().includes(q))) {
          out.push({ ...p, page: Number(pg) });
          if (out.length >= 60) return out;
        }
      }
    }
    return out;
  }, [query]);

  const section = sections[sectionIdx];

  const subsWithProducts = useMemo(
    () =>
      section.subs.map((sub) => {
        const products = [];
        for (let p = sub.from; p <= sub.to; p++) {
          const list = catalogData.pages[String(p)] || [];
          list.forEach((prod) => products.push({ ...prod, page: p }));
        }
        return { ...sub, products };
      }),
    [section]
  );

  const goToForm = () => {
    const el = document.getElementById("demande");
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  const isSelected = (product) => product.refs.some((r) => selection.some((s) => s.ref === r));

  return (
    <div className="bg-ink text-white min-h-screen">
      <Navbar />

      <main>
        <section className="relative pt-36 pb-16 overflow-hidden" data-testid="catalogue-hero">
          <div className="absolute inset-0 spotlight-blaze opacity-60" aria-hidden="true" />
          <div className="mx-auto max-w-7xl px-5 md:px-10 relative z-10">
            <RevealLine delay={0.1}>
              <span className="font-accent uppercase tracking-[0.4em] text-blaze text-sm">
                Reproduction fidèle — édition 2026
              </span>
            </RevealLine>
            <h1 className="mt-6 font-display leading-[0.85] tracking-tight text-[clamp(2.8rem,8vw,6.5rem)] uppercase">
              <RevealLine delay={0.25}>Catalogue</RevealLine>
              <RevealLine delay={0.4}>
                <span className="flex items-center gap-5 md:gap-8 flex-wrap">
                  <span className="text-outline">Tremblay 2026</span>
                  <img
                    src="/img/logo-tremblay.png"
                    alt="Logo Tremblay"
                    className="h-12 md:h-16 lg:h-20 w-auto rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
                    data-testid="catalogue-logo-tremblay"
                  />
                </span>
              </RevealLine>
            </h1>
            <FadeUp delay={0.65} className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => openPage(1)}
                data-testid="catalogue-open-flipbook"
                className="group px-8 py-4 bg-blaze text-white font-accent uppercase tracking-[0.22em] text-sm flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(200,16,46,0.4)] transition-all duration-200"
              >
                <BookOpen className="h-4 w-4" /> Feuilleter le catalogue
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href={`${API}/catalogue/download`}
                download
                data-testid="catalogue-download-pdf"
                className="flex items-center gap-3 px-8 py-4 border border-white/20 text-white font-accent uppercase tracking-[0.22em] text-sm hover:border-blaze hover:text-blaze transition-colors duration-200"
              >
                <FileDown className="h-4 w-4" /> Télécharger le PDF
              </a>
            </FadeUp>
          </div>
        </section>

        <div className="sticky top-[72px] z-40 bg-black/70 backdrop-blur-xl border-y border-white/10" data-testid="catalogue-section-nav">
          <SectionNav
            sections={sections}
            activeIdx={query ? -1 : sectionIdx}
            onSelect={(i) => {
              setSectionIdx(i);
              setQuery("");
              const el = document.getElementById("vitrine");
              if (window.__lenis) window.__lenis.scrollTo(el, { offset: -150 });
              else el?.scrollIntoView({ behavior: "smooth" });
            }}
          />
        </div>

        <section id="vitrine" className="py-16 md:py-20" data-testid="catalogue-vitrine">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
              <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight">
                {query ? "Résultats de recherche" : `${String(section.num).padStart(2, "0")} — ${section.name}`}
              </h2>
              <label className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  data-testid="catalogue-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un produit ou une réf..."
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-none h-12 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze"
                />
              </label>
            </div>

            {results !== null ? (
              results.length === 0 ? (
                <div data-testid="catalogue-search-empty" className="border border-dashed border-white/15 p-12 text-center text-white/50">
                  Aucun produit trouvé pour « {query} ». Essayez une autre référence (ex : TE1502).
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {results.map((p, i) => (
                    <ProductCard
                      key={`${p.name}-${i}`}
                      product={p}
                      onOpenPage={openPage}
                      testid="search-product-card"
                      selected={isSelected(p)}
                      selectedRefs={p.refs.filter((r) => selection.some((s) => s.ref === r))}
                      onToggle={toggleSelection}
                      onToggleRef={toggleRef}
                    />
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-16">
                {subsWithProducts.map((sub) => (
                  <div key={sub.name}>
                    <FadeUp className="flex items-baseline gap-4 mb-7">
                      <h3 className="font-accent uppercase tracking-[0.3em] text-xl text-white">{sub.name}</h3>
                      <span className="font-mono text-xs text-white/35">
                        Pages {sub.from}–{sub.to}
                      </span>
                      <span className="h-px flex-1 bg-white/10 self-center" />
                    </FadeUp>
                    {sub.products.length === 0 ? (
                      <FadeUp className="border border-dashed border-white/15 p-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-white/50">
                        <p className="text-sm">Produits illustrés dans le catalogue — consultez les pages directement.</p>
                        <button
                          onClick={() => openPage(sub.from)}
                          data-testid={`sub-open-page-${sub.from}`}
                          className="flex items-center gap-2 font-accent uppercase tracking-[0.2em] text-xs text-white/70 hover:text-blaze transition-colors shrink-0"
                        >
                          <BookOpen className="h-4 w-4" /> Page {sub.from}
                        </button>
                      </FadeUp>
                    ) : (
                      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {sub.products.map((p, i) => (
                          <ProductCard
                            key={`${p.name}-${i}`}
                            product={p}
                            onOpenPage={openPage}
                            testid="section-product-card"
                            selected={isSelected(p)}
                            selectedRefs={p.refs.filter((r) => selection.some((s) => s.ref === r))}
                            onToggle={toggleSelection}
                            onToggleRef={toggleRef}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-16 md:py-20 border-t border-white/10" data-testid="why-ligeria-section">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <FadeUp className="mb-12">
              <p className="font-accent uppercase tracking-[0.35em] text-blaze text-sm">Votre interlocuteur unique</p>
              <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight mt-4">
                Pourquoi passer par Ligéria ?
              </h2>
              <p className="mt-5 max-w-2xl text-white/60 leading-relaxed font-light">
                Vous commandez le bon produit, au bon tarif, sans naviguer seul dans un
                catalogue de plus de 1 500 références.
              </p>
            </FadeUp>
            <div className="grid gap-5 md:grid-cols-3">
              <FadeUp className="bg-panel border border-white/10 p-8 hover:border-blaze/50 transition-colors duration-300" delay={0}>
                <span className="font-display text-blaze text-2xl">01</span>
                <h3 className="font-accent uppercase tracking-[0.2em] text-lg mt-5">Le conseil de pro</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed font-light">
                  Décrivez votre besoin en une phrase : nous identifions pour vous le bon
                  produit, le bon coloris et la bonne taille dans le catalogue.
                </p>
              </FadeUp>
              <FadeUp className="bg-panel border border-white/10 p-8 hover:border-blaze/50 transition-colors duration-300" delay={0.1}>
                <span className="font-display text-blaze text-2xl">02</span>
                <h3 className="font-accent uppercase tracking-[0.2em] text-lg mt-5">Vos tarifs négociés</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed font-light">
                  Pas de prix affichés : vous recevez un devis clair, adapté à votre
                  structure et à votre volume, avant de vous engager.
                </p>
              </FadeUp>
              <FadeUp className="bg-panel border border-white/10 p-8 hover:border-blaze/50 transition-colors duration-300" delay={0.2}>
                <span className="font-display text-blaze text-2xl">03</span>
                <h3 className="font-accent uppercase tracking-[0.2em] text-lg mt-5">Le suivi de A à Z</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed font-light">
                  De la demande à la livraison, un seul interlocuteur suit votre dossier
                  et reste joignable à chaque étape.
                </p>
              </FadeUp>
            </div>
          </div>
        </section>

        <section id="demande" className="py-24 md:py-32 border-t border-white/10 bg-[#0A0A0A]" data-testid="quote-section">
          <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-14">
            <FadeUp className="lg:col-span-5">
              <p className="font-accent uppercase tracking-[0.35em] text-blaze text-sm">Demande de devis</p>
              <h2 className="mt-6 font-display uppercase leading-[0.9] tracking-tight text-4xl md:text-6xl">
                Votre demande,
                <br />
                <span className="text-outline">notre devis.</span>
              </h2>
              <p className="mt-8 text-white/60 leading-relaxed font-light max-w-md">
                Sélectionnez vos produits dans le catalogue ci-dessus ou saisissez la
                référence directement. Aucun paiement en ligne : nous vous répondons avec
                un devis adapté à votre structure. Frais de port offerts dès 370 € HT
                (sur devis en dessous) — livraison sous 4 jours à validation de votre devis.
              </p>
              <a
                href={PANTHER_LINKS.questions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="catalogue-panther-pastille"
                className="mt-10 flex items-center gap-4 border border-[#C32A3A] bg-black/40 p-5 hover:border-blaze transition-colors duration-200 group max-w-md"
              >
                <img src="/logo-panther.jpg" className="h-12 w-12 rounded-md object-cover shrink-0" alt="Logo PANTHER" />
                <span>
                  <span className="block font-accent uppercase tracking-[0.2em] text-sm text-white">
                    Vous cherchez un produit PANTHER ?
                  </span>
                  <span className="block text-xs text-white/50 mt-1 group-hover:text-blaze transition-colors">
                    Accédez au formulaire officiel PANTHER (réponse sous 24 h) →
                  </span>
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.15} className="lg:col-span-7">
              <QuoteForm
                refsAll={catalogData.refsAll}
                selection={selection}
                onRemoveRow={removeRow}
                onClearSelection={() => persist([])}
                onSetQty={setSelectionQty}
                onSetRowRef={setRowRef}
                onAddRow={addRow}
              />
            </FadeUp>
          </div>
        </section>
      </main>

      <Footer />

      <AnimatePresence>
        {selection.length > 0 && (
          <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] bg-panel border border-blaze/60 shadow-[0_10px_40px_rgba(0,0,0,0.5)] px-5 py-3 flex items-center gap-4 w-[calc(100%-2rem)] max-w-lg"
            data-testid="selection-bar"
          >
            <ShoppingBasket className="h-5 w-5 text-blaze shrink-0" />
            <span className="text-sm text-white/80 flex-1 leading-tight">
              <strong className="text-white">{selection.length}</strong> produit{selection.length > 1 ? "s" : ""} dans ma demande
            </span>
            <button
              onClick={() => persist([])}
              data-testid="selection-clear"
              className="p-1.5 text-white/40 hover:text-blaze transition-colors"
              aria-label="Vider la sélection"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              onClick={goToForm}
              data-testid="selection-to-form"
              className="flex items-center gap-2 bg-blaze text-white font-accent uppercase tracking-[0.18em] text-xs px-4 py-2.5 hover:-translate-y-0.5 transition-transform duration-200 shrink-0"
            >
              Mon formulaire
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {flip.open && (
          <Flipbook
            open={flip.open}
            page={flip.page}
            pageCount={catalogData.pageCount}
            onClose={() => setFlip((f) => ({ ...f, open: false }))}
            onChange={() => {}}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
